import React, { PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './LandingHero.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { useWidthContext } from '../../providers/Width';
import { ModalContext } from '../../providers/Modal/Modal';
import { DigitalBankTermsData } from '../DigitalBankTermsModal/DigitalBankTermsModal';
import withAsyncImport from '../../withAsyncImport';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { IDigitalBankTerms, IDigitalBankTermsRow, IRate, ITerm } from '../../types/shared/DigitalBankTerms';
import { useTermRatesContext } from '../../providers/TermRates/TermRates';
import { retrieveDBOsgiUrl, safeParse, wrapTrademarkSuperscript } from 'utils/cob-utils';
import { Button, Row, Col, Title, Image, Section, TextLine, List } from '../../importCL';
import InputCheckbox from '../../component-library/atoms/InputCheckbox';
import SlideDown from '../SlideDown/SlideDown';
import arrow from '../../component-library/assets/icons/grayArrow.svg'

const DigitalBankTermsModal = withAsyncImport(() => import('../DigitalBankTermsModal/DigitalBankTermsModal'));

interface BulletPoints {
    value?: string;
}
interface IProductTile {
    type?: 'highYield' | 'bumpUp' | 'jumbo';
    title?: string;
    apy?: string;
    term?: string;
    termText?: string;
    termUrl?: string;
    desc?: string;
    minBalance?: string;
    ctaText?: string;
    ctaUrl?: string;
    ctaId?: string;
    subCtaText?: string;
    subCtaUrl?: string;
    bulletPoint?: BulletPoints[];
}

interface ILandingHeroProps extends MappedComponentProperties {
    header?: string;
    paragraphText?: string;
    buttonText?: string;
    buttonId?: string;
    lowerLeftImage?: string;
    lowerLeftImageAltText?: string;
    largeImage?: string;
    largeImageAltText?: string;
    tiles?: IProductTile[];
    usingMetaData?: boolean;
    showNewDesign?: boolean;
}

export const LandingHeroEditConfig = {
    emptyLabel: 'Landing Hero',
    isEmpty: (props: PropsWithChildren<ILandingHeroProps>) => {
        return !props || !props.header;
    },
};

const LandingHero = (props: PropsWithChildren<ILandingHeroProps>): JSX.Element => {
    const { width } = useWidthContext();
    const { termRates, getFeaturedItem } = useTermRatesContext();
    const { setModalOpen, setModalContent, setModalClass, isOpen } = useContext(ModalContext);
    const [featuredHYSA, setFeaturedHYSA] = useState<IRate>({});
    const [featuredJumbo, setFeaturedJumbo] = useState<IRate>({});
    const [featuredBump, setFeaturedBump] = useState<IRate>({});
    const [dbTermsData, setDbTermsData] = useState<IDigitalBankTerms>();
    const buttonUrl = retrieveDBOsgiUrl(safeParse(PathUtils.getMetaPropertyValue('digitalBankTerms'))?.baseUrl);
    const isMobile = width <= 768;
    const [selectedTileIndex, setSelectedTileIndex] = useState<number | null>(isMobile? null : 0);
    const selectedTile = selectedTileIndex !=null ? props.tiles[selectedTileIndex] : null;
    
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    }, [isOpen]);

    const getDigitalBankTermType = (ctaId: string): keyof IDigitalBankTerms | null => {
        if (ctaId === 'Deposit_FtRateJumboSubmit') {
            return 'jumboCd';
        } else if (ctaId === 'Deposit_FtRateBumpSubmit') {
            return 'bumpUpCd';
        } else if (ctaId === 'Deposit_FtRateHYSASubmit') {
            return 'highYieldSavings';
        }
        return null;
    };

    const getParentKey = (ctaId: string): keyof IDigitalBankTerms | null => {
        if (ctaId === 'Deposit_FtRateJumboSubmit') {
            return 'jumboCdTerm';
        } else if (ctaId === 'Deposit_FtRateBumpSubmit') {
            return 'bumpCdTerm';
        } else if (ctaId === 'Deposit_FtRateHYSASubmit') {
            return 'highYieldSavingsTerm';
        }
        return null;
    };

    const handleModalClick = async (terms?: ITerm, rates?: IRate[], ctaId?: string) => {

        const mapTable = (terms: ITerm, rates: IRate[]) => {
            const mappedTerms: DigitalBankTermsData = terms;
            const mappedTableRows: IDigitalBankTermsRow[] = [];

            rates.map((rate) => {
                mappedTableRows.push({
                    columnOne: rate.term,
                    columnTwo: rate.apy,
                    columnThreeText: dbTermsData?.urlLabelText ?? 'Open an Account',
                    columnThreeLink: retrieveDBOsgiUrl(rate.url),
                });
            });

            mappedTerms.tableRows = mappedTableRows.sort((a: IDigitalBankTermsRow, b: IDigitalBankTermsRow) => parseInt(a.columnOne!) - parseInt(b.columnOne!))
                .map((term: IDigitalBankTermsRow) => {
                    return { ...term, columnOne: term.columnOne + ' Months', columnTwo: term.columnTwo + '%' };
                });
            return mappedTerms;
        };

        if (!terms && !rates) {
            return;
        }

        let termsData;

        if (props.usingMetaData && terms && rates) {
            termsData = mapTable(terms, rates);
        } else {
            const termsKey = getDigitalBankTermType(ctaId as string);
            const parentKey = getParentKey(ctaId as string);
            const termsMetadataColumns = JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string);

            let mappedTermsData;

            if (!termsKey) {
                return;
            }

            const terms = (termRates[termsKey] as IRate[])
                .sort((a, b) => parseInt(a?.term ?? '') - parseInt(b?.term ?? ''))
                .map((product) => ({
                    columnOne: parseInt(product?.term ?? '').toString() + (product?.term === '1' ? ' Month' : ' Months'),
                    columnTwo: (product?.apy ?? '0.00') + '%',
                    columnThreeText: 'Open an Account',
                    columnThreeLink: (product as IRate)?.url ?? '',
                }));

            if (parentKey) {
                const data = (termsMetadataColumns[parentKey]);
                mappedTermsData = {
                    title: data.title,
                    subtitle: data.subtitle,
                    columnOneLabel: data.columnOneLabel,
                    columnTwoLabel: data.columnTwoLabel,
                    columnThreeLabel: data.columnThreeLabel,
                    tableRows: terms,
                };
            }
            termsData = mappedTermsData;
        }
        const TileTermsModal = (
            <DigitalBankTermsModal termsData={termsData} closeCallback={() => setModalOpen(false)} />
        );

        setModalContent(TileTermsModal!);
        setModalClass('TileTermsModal');
        setModalOpen(true);
    };

    const handleSubCtaClick = (anchorPath?: string) => {
        if (anchorPath) {
            const anchor = document.querySelector(`.${anchorPath}`);
            if (anchor) {
                anchor.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const getFeaturedRate = (rates: IRate[]) => {
        if (!rates) {
            return {};
        }
        return rates.find((rate: any) => rate.featuredRate) || {};
    };


    const isObjNotEmpty = (obj: IRate) => {
        return (typeof obj !== 'undefined' && obj !== null && Object.keys(obj).length !== 0);
    };

    const handleSelectedTile = useCallback(() => {
        if (selectedTile === null || !props.tiles) {
            return null
        }
        return renderTileInfo(props.tiles[selectedTileIndex])
    },[selectedTileIndex, featuredHYSA, featuredBump, featuredJumbo])

    const renderTileInfo = (tile: IProductTile) => {
        let termsRow: any | '';
        let modalRates: any = '';

        if (tile.type === 'highYield' && isObjNotEmpty(featuredHYSA)) {
            tile.apy = featuredHYSA.apy;
            tile.term = '';
            tile.ctaUrl = retrieveDBOsgiUrl(featuredHYSA.url);
            termsRow = dbTermsData?.highYieldSavingsTerm ?? '';
            modalRates = dbTermsData?.highYieldSavings;
        } else if (tile.type === 'bumpUp' && isObjNotEmpty(featuredBump)) {
            tile.apy = featuredBump.apy;
            tile.term = featuredBump.term;
            tile.ctaUrl = retrieveDBOsgiUrl(featuredBump.url);
            termsRow = dbTermsData?.bumpCdTerm ?? '';
            modalRates = dbTermsData?.bumpUpCd;
        } else if (tile.type === 'jumbo' && isObjNotEmpty(featuredJumbo)) {
            tile.apy = featuredJumbo.apy;
            tile.term = featuredJumbo.term;
            tile.ctaUrl = retrieveDBOsgiUrl(featuredJumbo.url);
            termsRow = dbTermsData?.jumboCdTerm ?? '';
            modalRates = dbTermsData?.jumboCd;
        }

        return (
            <div className='flex md:flex-col bg-blue-creditone max-w-[39.125rem] md:mt-4'>
                <div className='flex justify-center items-center md:items-start md:mt-[-0.1rem] min-h-16'>
                    <img className='-rotate-90 md:rotate-0 h-[2.8125rem] md:h-[1.25rem] ml-[-2.2rem] md:m-0' src={arrow} />
                </div>
                <div className='ml-4 mr-8 mt-8 md:mt-0 md:mr-4 max-w-[30rem]'>
                    <h3 className='text-white font-bold mb-6 md:text-lg'>{transformHTMLParserLinks(tile.title)}</h3>
                    <div className='flex mb-2'>
                        <p className='text-[4rem] md:text-[3.5rem] text-white font-bold mt-4'>{tile.apy && transformHTMLParserLinks(tile.apy)}%</p>
                        <div className='flex flex-col ml-4'>
                            <span className='text-lg text-white font-bold'> APY
                                <sup>*
                                </sup>
                            </span>
                            {tile.term && <p className='text-white font-bold !m-0 leading-[0.875rem]'>{tile.term}-Month Term</p>}
                            <a className='text-white text-sm' onClick={() => handleModalClick(termsRow, modalRates, tile.ctaId)}>{tile.termText}</a>
                        </div>
                    </div>
                    <div className='max-w-[90%] md:max-w-[100%] pb-10'>
                        <p className='text-white'>{tile.desc && transformHTMLParserLinks(tile.desc)}</p>
                        <hr></hr>
                        {tile.bulletPoint && (
                            <List
                                className='!p-0'
                                textClass='!text-white !mb-4'
                                checks
                                items={tile.bulletPoint?.map((bullet)=> bullet.value)}
                            />
                        )}
                        <hr></hr>
                        <p className='!text-white text-xs'>{tile.minBalance && transformHTMLParserLinks(tile.minBalance)}</p>
                        <Button 
                            href={tile.ctaUrl} 
                            variant='ghosted-light'
                            size={isMobile ? 'medium' : 'large'}
                            id={tile.ctaId}>
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(tile.ctaText)) as unknown as ReactElement}
                        </Button>

                    </div>
                </div>
            </div>
        )
    }

    const handleTiles = useCallback(() => {
        return (<>
            {props.tiles?.map((authoredTile, index, tiles) => {
                const tile = authoredTile;
                let termsRow: any | '';
                let modalRates: any = '';

                if (tile.type === 'highYield' && isObjNotEmpty(featuredHYSA)) {
                    tile.apy = featuredHYSA.apy;
                    tile.term = '';
                    tile.ctaUrl = retrieveDBOsgiUrl(featuredHYSA.url);
                    termsRow = dbTermsData?.highYieldSavingsTerm ?? '';
                    modalRates = dbTermsData?.highYieldSavings;
                } else if (tile.type === 'bumpUp' && isObjNotEmpty(featuredBump)) {
                    tile.apy = featuredBump.apy;
                    tile.term = featuredBump.term;
                    tile.ctaUrl = retrieveDBOsgiUrl(featuredBump.url);
                    termsRow = dbTermsData?.bumpCdTerm ?? '';
                    modalRates = dbTermsData?.bumpUpCd;
                } else if (tile.type === 'jumbo' && isObjNotEmpty(featuredJumbo)) {
                    tile.apy = featuredJumbo.apy;
                    tile.term = featuredJumbo.term;
                    tile.ctaUrl = retrieveDBOsgiUrl(featuredJumbo.url);
                    termsRow = dbTermsData?.jumboCdTerm ?? '';
                    modalRates = dbTermsData?.jumboCd;
                }


                return (
                    <Col className={`LandingHero__tileCol ${index !== tiles.length - 1 ? 'LandingHero__verticalLine' : ''}`} key={index}>
                        <div className='LandingHero__tile'>
                            <div className='LandingHero__apyContainer'>
                                <Title className='LandingHero__tileTitle' tag='h5'>{tile.title && transformHTMLParserLinks(tile.title)}</Title>
                                <div className='LandingHero__textContainer'>
                                    <div className='LandingHero__apy'>{tile.apy && transformHTMLParserLinks(tile.apy)}</div>
                                    <div className='LandingHero__apyTextContainer'>
                                        <span className='LandingHero__percent'>%</span>
                                        <span className='LandingHero__apyText'>APY<sup>*</sup></span>
                                    </div>
                                </div>
                            </div>
                            <div className='LandingHero__termContainer'>
                                {tile.term && <p className='LandingHero__termText'>{tile.term} Month Term | <a className='LandingHero__termTextLink' onClick={() => handleModalClick(termsRow, modalRates, tile.ctaId)}>{tile.termText}</a></p>}
                                <div className={`LandingHero__fillerDiv${tile.term ? '--reg' : '--space'}`}></div>
                            </div>
                            <p className='LandingHero__tileDesc'>{tile.desc && transformHTMLParserLinks(tile.desc)}</p>
                            <Button className='LandingHero__tileCta' href={tile.ctaUrl} id={tile.ctaId}>{transformHTMLParserLinks(wrapTrademarkSuperscript(tile.ctaText)) as unknown as ReactElement}</Button>
                            <LinkWrapper onClick={() => handleSubCtaClick(tile.subCtaUrl)}>
                                <p className='LandingHero__tileSubCta'>{transformHTMLParserLinks(wrapTrademarkSuperscript(tile.subCtaText))}</p>
                            </LinkWrapper>
                        </div>
                    </Col>
                );
            })}
        </>);
    }, [featuredHYSA, featuredBump, featuredJumbo]);

    useEffect(() => {
        if (props.usingMetaData) {
            const dbTerms = JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string);
            setFeaturedHYSA(getFeaturedRate(dbTerms.highYieldSavings) as IRate);
            setFeaturedJumbo(getFeaturedRate(dbTerms.jumboCd) as IRate);
            setFeaturedBump(getFeaturedRate(dbTerms.bumpUpCd) as IRate);
            setDbTermsData(dbTerms);
        } else if (Object.keys(termRates).length > 0) {
            setFeaturedHYSA(getFeaturedItem('highYieldSavings'));
            setFeaturedJumbo(getFeaturedItem('jumboCd'));
            setFeaturedBump(getFeaturedItem('bumpUpCd'));
            setDbTermsData(termRates);
        }
    }, [termRates]);

    if (props.showNewDesign) {
        return (
            <div className='bg-grey-alabaster flex justify-center px-12 md:px-0'>
                <div className='pt-12 md:pt-8 mr-28 md:mr-0 max-w-[29.125rem] md:pb-4'>
                    <TextLine 
                        variant='author'
                        tag='h2'
                        subHeading={props.paragraphText && transformHTMLParserLinks(props.paragraphText)}
                        className='mb-6 md:px-4'
                    >
                        {props.header && transformHTMLParserLinks(props.header)}
                    </TextLine>
                    <p className='text-lg font-bold text-grey-tundora md:px-4'>Choose A Product</p>
                    <div>
                        {props.tiles.map((tile, index) => {
                            return (
                                <React.Fragment key={index}>
                                    <InputCheckbox
                                        className='rounded-none md:mx-4'
                                        circularCheckbox
                                        label={tile.title ? transformHTMLParserLinks(tile.title) : undefined}
                                        setValue={(checked: boolean)=>{
                                            if (isMobile) {
                                                setSelectedTileIndex(checked? index : null)
                                            } else {
                                                if(checked) {
                                                    setSelectedTileIndex(index)
                                                }
                                            }
                                        }}
                                        value={selectedTileIndex === index}
                                        variant="quiz"
                                    />
                                    {isMobile && (
                                        <SlideDown
                                            open={selectedTileIndex === index}>
                                                {renderTileInfo(tile)}
                                        </SlideDown>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
                {width > 768 && handleSelectedTile()}
            </div>
        )
    } else {
        return (
            <div id='landing-hero' className='LandingHero'>
                <Section className='LandingHero__section' bgColor='blue-denim'>
                    <Row className='LandingHero__row'>
                        <Col lg={7} className='LandingHero__leftCol'>
                            <Title className='LandingHero__header'>{props.header && transformHTMLParserLinks(props.header)}</Title>
                            <p className='LandingHero__paragraph'>{props.paragraphText && transformHTMLParserLinks(props.paragraphText)}</p>
                            <div>
                                <Button size='large' className='LandingHero__ctaButton' variant='ghosted-light' href={buttonUrl} id={props.buttonId}>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.buttonText)) as unknown as ReactElement}</Button>
                            </div>
                            <div>
                                <Image loading='lazy' className='LandingHero__lowerImage' src={props.lowerLeftImage} alt={props.lowerLeftImageAltText} />
                            </div>
                        </Col>
                        {width > 1200 && <Col lg={5} className='LandingHero__rightCol'>
                            <Image loading='lazy' className='LandingHero__largeImage' src={props.largeImage} alt={props.largeImageAltText} />
                        </Col>}
                    </Row>
                </Section>
                <div className='LandingHero__rowContainer'>
                    <Row className='LandingHero__tilesRow'>
                        {handleTiles()}
                    </Row>
                </div>
            </div>
        );
    };
    }
export default LandingHero;
